/**
 * Select field value rendering. Values store option ids; labels and colors come from the
 * field's config.
 *
 * - selectedOptionIds: the ids a stored value names.
 * - resolveSelectedOptions: those ids as option records.
 * - pickableOptions: the options a picker offers.
 */
import type {FieldValue, IFieldDefinition, ISelectConfig, ISelectOption} from '@shared/field_types';
import {SelectFrontStyle, asSelectConfig} from '@shared/field_types';
import type {IFieldBadge} from '@c/fields/format/badge_helpers';
import {plainBadge} from '@c/fields/format/badge_helpers';
import {isString} from 'lodash-es';

/** Get the option ids a stored value names, in the order stored. Empty for a cleared value. */
export function selectedOptionIds(value: FieldValue): string[] {
   if (value == null) {
      return [];
   }
   if (Array.isArray(value)) {
      return value.filter(isString);
   }
   return isString(value) && value !== '' ? [value] : [];
}

/**
 * Get the selected options in the order the value stores them. Archived options still
 * resolve; an id naming no option on this field is dropped.
 */
export function resolveSelectedOptions(
   config: ISelectConfig | null,
   value: FieldValue,
): ISelectOption[] {
   if (config == null) {
      return [];
   }
   const by_id = new Map(config.options.map((option) => [option.id, option]));

   return selectedOptionIds(value)
      .map((id) => by_id.get(id) ?? null)
      .filter((option): option is ISelectOption => option != null);
}

/**
 * Get the options a picker offers: the live options, plus any archived option the value
 * already names so an existing choice stays visible.
 */
export function pickableOptions(config: ISelectConfig | null, value: FieldValue): ISelectOption[] {
   if (config == null) {
      return [];
   }
   const selected = new Set(selectedOptionIds(value));

   return config.options.filter((option) => !option.archived || selected.has(option.id));
}

/** Get the badges selected options render as; SINGLE_BADGE collapses many to one plus a count. */
export function buildSelectBadges(field: IFieldDefinition, value: FieldValue): IFieldBadge[] {
   const config = asSelectConfig(field.type, field.config);

   const badges = resolveSelectedOptions(config, value).map((option) => ({
      ...plainBadge(option.label),
      color: option.color ?? config?.optionFallbackColor ?? null,
   }));

   // SINGLE_BADGE collapses a multi-value render to the first option plus a count.
   if (config?.frontStyle === SelectFrontStyle.SINGLE_BADGE && badges.length > 1) {
      return [{...badges[0], text: `${badges[0].text} +${badges.length - 1}`}];
   }

   return badges;
}
